import type { GetSettings200 } from '@ipfabric/api-client/client/schemas/getSettings200';
import type {
  AppSnapshots,
  AppSnapshotsRetention,
  settingTypes,
} from '@ipfabric/utils';
import { DEFAULT_APP_SETTING } from '@ipfabric/utils';

export type SnapshotCollectionValues = {
  snapshots: AppSnapshots;
  snapshotsRetention: AppSnapshotsRetention;
};

const defaultSnapshots = DEFAULT_APP_SETTING.snapshots;
const defaultSnapshotsRetention = DEFAULT_APP_SETTING.snapshotsRetention;

const ensureNumber = (value: number | null | undefined, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

type CronInput =
  | settingTypes.AppSnapshots['cron']
  | settingTypes.AppSnapshotsRetention['action']['cron']
  | { expression?: string; timezone?: string }
  | undefined;

const sanitizeCron = (
  cron: CronInput,
  fallback: settingTypes.AppSnapshots['cron'],
) => {
  const expression = cron?.expression ?? fallback.expression;
  const timezone = cron?.timezone ?? fallback.timezone;
  return { expression, timezone };
};

const prepareSnapshots = (
  snapshots: GetSettings200['snapshots'] | undefined,
): AppSnapshots => ({
  enabled: snapshots?.enabled ?? defaultSnapshots.enabled,
  cron: sanitizeCron(snapshots?.cron, defaultSnapshots.cron),
  maxLoaded: ensureNumber(snapshots?.maxLoaded, defaultSnapshots.maxLoaded),
  maxLocked: ensureNumber(snapshots?.maxLocked, defaultSnapshots.maxLocked),
});

const prepareSnapshotsRetention = (
  snapshotsRetention: GetSettings200['snapshotsRetention'] | undefined,
): AppSnapshotsRetention => ({
  all: {
    enabled:
      snapshotsRetention?.all?.enabled ?? defaultSnapshotsRetention.all.enabled,
    days: ensureNumber(
      snapshotsRetention?.all?.days,
      defaultSnapshotsRetention.all.days,
    ),
  },
  firstOfDay: {
    enabled:
      snapshotsRetention?.firstOfDay?.enabled ??
      defaultSnapshotsRetention.firstOfDay.enabled,
    days: ensureNumber(
      snapshotsRetention?.firstOfDay?.days,
      defaultSnapshotsRetention.firstOfDay.days,
    ),
  },
  firstOfWeek: {
    enabled:
      snapshotsRetention?.firstOfWeek?.enabled ??
      defaultSnapshotsRetention.firstOfWeek.enabled,
    weeks: ensureNumber(
      snapshotsRetention?.firstOfWeek?.weeks,
      defaultSnapshotsRetention.firstOfWeek.weeks,
    ),
  },
  firstOfMonth: {
    enabled:
      snapshotsRetention?.firstOfMonth?.enabled ??
      defaultSnapshotsRetention.firstOfMonth.enabled,
    months: ensureNumber(
      snapshotsRetention?.firstOfMonth?.months,
      defaultSnapshotsRetention.firstOfMonth.months,
    ),
  },
  firstOfYear: {
    enabled:
      snapshotsRetention?.firstOfYear?.enabled ??
      defaultSnapshotsRetention.firstOfYear.enabled,
    years: ensureNumber(
      snapshotsRetention?.firstOfYear?.years,
      defaultSnapshotsRetention.firstOfYear.years,
    ),
  },
  removalMetrics: {
    hddUtilization: ensureNumber(
      snapshotsRetention?.removalMetrics?.hddUtilization,
      defaultSnapshotsRetention.removalMetrics.hddUtilization,
    ),
    snapshotsCount: ensureNumber(
      snapshotsRetention?.removalMetrics?.snapshotsCount,
      defaultSnapshotsRetention.removalMetrics.snapshotsCount,
    ),
  },
  action: {
    command:
      snapshotsRetention?.action?.command ??
      defaultSnapshotsRetention.action.command,
    cron: sanitizeCron(
      snapshotsRetention?.action?.cron,
      defaultSnapshotsRetention.action.cron,
    ),
  },
});

type SnapshotCollectionInput = {
  snapshots?: GetSettings200['snapshots'];
  snapshotsRetention?: GetSettings200['snapshotsRetention'];
};

export const prepareSnapshotCollectionValues = (
  values?: SnapshotCollectionInput,
): SnapshotCollectionValues => ({
  snapshots: prepareSnapshots(values?.snapshots),
  snapshotsRetention: prepareSnapshotsRetention(values?.snapshotsRetention),
});
